import { useState } from "react";
import {
  ArchiveRestore,
  ArchiveX,
  ChevronRight,
  Folder,
  FolderOpen,
  Lock,
  Pencil,
} from "lucide-react";
import type { DocumentFolderTreeDto } from "@/types/api";
import { cn } from "@/design-system/lib/utils";
import { RowActions } from "@/components/ux/RowActions";

export interface FolderTreeAction {
  /** Stable id of the folder the user clicked. */
  folderId: string;
  /** The folder DTO so callers can read isArchived / canEdit / name. */
  folder: DocumentFolderTreeDto;
}

interface FolderTreeProps {
  nodes: DocumentFolderTreeDto[];
  selectedId?: string | null;
  onSelect: (node: DocumentFolderTreeDto) => void;
  initiallyExpanded?: boolean;
  filter?: string;
  /**
   * Folder that can't be chosen (e.g. a document's current folder when
   * moving). The row still expands but renders muted and never selects.
   */
  disabledFolderId?: string | null;
  /**
   * Folder Rename & Archive v1.0 — fired when the user picks "Rename" from
   * a folder's kebab menu. Caller renders the rename UX (inline edit,
   * modal, …). When undefined, the menu item is hidden.
   */
  onRenameRequested?: (action: FolderTreeAction) => void;
  /** Fired when the user picks Archive from an active folder's menu. */
  onArchiveRequested?: (action: FolderTreeAction) => void;
  /** Fired when the user picks Unarchive from an archived folder's menu. */
  onUnarchiveRequested?: (action: FolderTreeAction) => void;
  /**
   * Folder Visibility & Access Control v1.0 — fired when the user picks
   * "Permissions". The menu item only renders for folders whose
   * <code>canManageAccess</code> flag is set (ADMIN). When undefined, hidden.
   */
  onPermissionsRequested?: (action: FolderTreeAction) => void;
  /**
   * Id of the folder currently being inline-renamed. The matching row
   * renders an &lt;input&gt; instead of its name; everything else stays static.
   */
  renamingFolderId?: string | null;
  /** Renders the inline rename input for a folder. */
  renderRenameInput?: (folder: DocumentFolderTreeDto) => React.ReactNode;
}

/**
 * Lightweight, fully controlled folder tree. Children are lazy-rendered
 * only after the row is expanded, so large trees remain fast.
 *
 * Folder Rename & Archive v1.0 — each row carries a "..." kebab menu
 * with Rename and Archive/Unarchive actions when the folder's
 * <code>canEdit</code> flag is set. Archived folders render at 50%
 * opacity with an "Archived" badge.
 */
export function FolderTree({
  nodes,
  selectedId,
  onSelect,
  initiallyExpanded = false,
  filter,
  disabledFolderId,
  onRenameRequested,
  onArchiveRequested,
  onUnarchiveRequested,
  onPermissionsRequested,
  renamingFolderId,
  renderRenameInput,
}: FolderTreeProps) {
  return (
    <ul role="tree" className="flex flex-col gap-space-1">
      {nodes.map((node) => (
        <FolderTreeNode
          key={node.id}
          node={node}
          depth={0}
          selectedId={selectedId}
          onSelect={onSelect}
          initiallyExpanded={initiallyExpanded}
          filter={filter?.toLowerCase().trim() || ""}
          disabledFolderId={disabledFolderId}
          onRenameRequested={onRenameRequested}
          onArchiveRequested={onArchiveRequested}
          onUnarchiveRequested={onUnarchiveRequested}
          onPermissionsRequested={onPermissionsRequested}
          renamingFolderId={renamingFolderId}
          renderRenameInput={renderRenameInput}
        />
      ))}
    </ul>
  );
}

interface NodeProps {
  node: DocumentFolderTreeDto;
  depth: number;
  selectedId?: string | null;
  onSelect: (node: DocumentFolderTreeDto) => void;
  initiallyExpanded: boolean;
  filter: string;
  disabledFolderId?: string | null;
  onRenameRequested?: (action: FolderTreeAction) => void;
  onArchiveRequested?: (action: FolderTreeAction) => void;
  onUnarchiveRequested?: (action: FolderTreeAction) => void;
  onPermissionsRequested?: (action: FolderTreeAction) => void;
  renamingFolderId?: string | null;
  renderRenameInput?: (folder: DocumentFolderTreeDto) => React.ReactNode;
}

function FolderTreeNode({
  node,
  depth,
  selectedId,
  onSelect,
  initiallyExpanded,
  filter,
  disabledFolderId,
  onRenameRequested,
  onArchiveRequested,
  onUnarchiveRequested,
  onPermissionsRequested,
  renamingFolderId,
  renderRenameInput,
}: NodeProps) {
  const [expanded, setExpanded] = useState(initiallyExpanded || depth < 1);
  const hasChildren = node.children.length > 0;
  const isDisabled = disabledFolderId != null && node.id === disabledFolderId;

  const matches = filter === "" || node.name.toLowerCase().includes(filter);
  const childMatch =
    filter !== "" && hasDescendantMatching(node.children, filter);

  if (filter !== "" && !matches && !childMatch) {
    return null;
  }

  const isSelected = selectedId === node.id;
  const isRenaming = renamingFolderId === node.id && !!renderRenameInput;

  // canEdit drives whether the kebab is rendered at all (no greyed-out
  // disabled items for permission denials). Within an editable row, the
  // active/archived state picks Archive vs. Unarchive.
  // Permissions is an ADMIN-only action (node.canManageAccess) and shows even
  // for EntityRoot folders; rename/archive stay gated by canEdit. The kebab
  // renders when ANY permitted action is available.
  const canManagePermissions = node.canManageAccess && !!onPermissionsRequested;
  const showKebab =
    (node.canEdit &&
      (onRenameRequested || onArchiveRequested || onUnarchiveRequested)) ||
    canManagePermissions;

  return (
    <li role="treeitem" aria-expanded={expanded}>
      <div
        className={cn(
          "group flex items-center gap-space-1 rounded-md transition-colors",
          isSelected ? "bg-gold-light" : "hover:bg-warm-white",
          node.isArchived && "opacity-50",
        )}
      >
        <button
          type="button"
          aria-disabled={isDisabled || undefined}
          onClick={() => {
            if (isRenaming) return;
            if (hasChildren) setExpanded((e) => !e);
            if (isDisabled) return;
            onSelect(node);
          }}
          className={cn(
            "flex items-center gap-space-2 flex-1 min-w-0 px-space-2 py-space-1 text-left text-small bg-transparent border-0",
            isSelected ? "text-onyx font-semibold" : "text-onyx",
            isDisabled && "opacity-50 cursor-default",
          )}
          style={{ paddingLeft: `${depth * 14 + 8}px` }}
        >
          {hasChildren ? (
            <ChevronRight
              size={14}
              aria-hidden
              className={cn("shrink-0 transition-transform", expanded && "rotate-90")}
            />
          ) : (
            <span className="w-[14px] shrink-0" aria-hidden />
          )}
          {expanded && hasChildren ? (
            <FolderOpen size={14} className="shrink-0 text-gold" aria-hidden />
          ) : (
            <Folder size={14} className="shrink-0 text-gold" aria-hidden />
          )}
          {isRenaming ? (
            <span className="flex-1 min-w-0">{renderRenameInput!(node)}</span>
          ) : (
            <span className="truncate flex-1">{node.name}</span>
          )}
          {!isRenaming && node.isRestricted && (
            <Lock
              size={12}
              className="shrink-0 text-gray-mid"
              aria-label="Restricted folder"
            />
          )}
          {!isRenaming && node.isArchived && (
            <span
              className="text-caption uppercase tracking-wide text-gray-mid border border-border-light rounded px-[6px] py-[1px]"
              aria-label="Archived folder"
            >
              Archived
            </span>
          )}
          {!isRenaming && !node.isArchived && node.documentCount > 0 && (
            <span className="text-caption uppercase tracking-wide text-gray-mid">
              {node.documentCount}
            </span>
          )}
        </button>
        {showKebab && !isRenaming && (
          <div className="pr-space-1">
            <RowActions
              label={`Actions for ${node.name}`}
              actions={[
                {
                  key: "rename",
                  label: "Rename",
                  icon: Pencil,
                  hidden: !onRenameRequested,
                  onClick: () =>
                    onRenameRequested?.({ folderId: node.id, folder: node }),
                },
                {
                  key: "archive",
                  label: "Archive",
                  icon: ArchiveX,
                  hidden: !onArchiveRequested || node.isArchived,
                  onClick: () =>
                    onArchiveRequested?.({ folderId: node.id, folder: node }),
                },
                {
                  key: "unarchive",
                  label: "Unarchive",
                  icon: ArchiveRestore,
                  hidden: !onUnarchiveRequested || !node.isArchived,
                  onClick: () =>
                    onUnarchiveRequested?.({ folderId: node.id, folder: node }),
                },
                {
                  key: "permissions",
                  label: "Permissions",
                  icon: Lock,
                  hidden: !canManagePermissions,
                  onClick: () =>
                    onPermissionsRequested?.({ folderId: node.id, folder: node }),
                },
              ]}
            />
          </div>
        )}
      </div>
      {expanded && hasChildren && (
        <ul role="group" className="flex flex-col gap-space-1 mt-space-1">
          {node.children.map((child) => (
            <FolderTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              initiallyExpanded={initiallyExpanded}
              filter={filter}
              disabledFolderId={disabledFolderId}
              onRenameRequested={onRenameRequested}
              onArchiveRequested={onArchiveRequested}
              onUnarchiveRequested={onUnarchiveRequested}
              onPermissionsRequested={onPermissionsRequested}
              renamingFolderId={renamingFolderId}
              renderRenameInput={renderRenameInput}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function hasDescendantMatching(
  nodes: DocumentFolderTreeDto[],
  filter: string,
): boolean {
  for (const n of nodes) {
    if (n.name.toLowerCase().includes(filter)) return true;
    if (hasDescendantMatching(n.children, filter)) return true;
  }
  return false;
}
